import type {PromotionFilter, PromotionFilterOption} from '../components/common/PromotionFilters/types';
import type {RadioChangeEvent} from '@ds/types';

import {EditOutlined} from '@ant-design/icons';
import {Button, Divider, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useEffect, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {getDiscountList, getFilters} from 'store/slices/promotion-center/thunk';

import PromotionFilters from '../components/common/PromotionFilters/PromotionFilters';
import {INITIAL_PROMOTION_FILTERS} from '../components/common/PromotionFilters/constants';
import DiscountViewInfo from './components/DiscountViewInfo/DiscountViewInfo';
import DiscountViewProductItem from './components/DiscountViewProductItem/DiscountViewProductItem';
import {DISCOUNT_VIEW_PAGE_SIZE, DISCOUNT_VIEW_ROUTES} from './constants';
import {getDiscountViewVendorId} from './utils';

const DiscountView: React.FC = () => {
  const vendorId = getDiscountViewVendorId();
  const [filters, setFilters] = useState<PromotionFilter[]>(() =>
    structuredClone(INITIAL_PROMOTION_FILTERS)
  );
  const dispatch = useAppDispatch();
  const params = useParams<{cycleId?: string}>();
  const navigate = useNavigate();
  const {loading, campaignCycle, categoriesList} = useAppSelector((state) => state.promotionCenter);
  const [filtersValue, setFiltersValue] = useState<Record<string, unknown>>({});
  const [pagination, setPagination] = useState({pageSize: DISCOUNT_VIEW_PAGE_SIZE, page: 1});

  const handleRadioChange = ({name, e}: {name: string; e: RadioChangeEvent}) => {
    setFiltersValue({
      ...filtersValue,
      [name]: e.target.value
    });
  };

  const activeFilterItem = (filter: PromotionFilter) => {
    setFilters((current) =>
      current.map((item) => {
        if (filter.name === item.name) {
          setFiltersValue({
            ...filtersValue,
            [filter.key ?? '']: !filter.selected ? item.name : null
          });
          return {...item, selected: !item.selected};
        }
        if (filter.key !== item.key) {
          return {...item, selected: item.selected};
        }
        return {...item, selected: false};
      })
    );
  };

  const handleFilter = ({
    selectedItem,
    name,
    checked
  }: {
    selectedItem: PromotionFilterOption;
    name: string;
    checked: boolean;
  }) => {
    setFilters((current) =>
      current.map((filter) => {
        if (filter.name !== name) {
          return filter;
        }
        return {
          ...filter,
          items: filter.items?.map((item) =>
            item.value === selectedItem.value ? {...item, selected: checked} : item
          )
        };
      })
    );
  };

  const getItems = ({pageSize, page}: {pageSize?: number; page?: number}) => {
    const preparedFilters: Record<string, unknown> = {
      category: []
    };

    filters.forEach((filter) => {
      if (filter.type === 'checkbox' && filter.items?.length) {
        filter.items.forEach((item) => {
          if (item.selected) {
            const current = preparedFilters[filter.name];
            if (!Array.isArray(current) || current.length === 0) {
              preparedFilters[filter.name] = [item.value];
            } else {
              current.push(item.value);
            }
          }
        });
      }
    });

    const sortValue = filtersValue.sort as PromotionFilterOption | undefined;
    if (sortValue) {
      preparedFilters.sort_type = sortValue.value;
      preparedFilters.sort_by = sortValue.name;
    }
    const stockValue = filtersValue.stock as PromotionFilterOption | boolean | undefined;
    if (stockValue && typeof stockValue === 'object') {
      preparedFilters.stock = stockValue.value;
    }
    if (filtersValue.discount_share_type) {
      preparedFilters.discount_share_type = 'brand';
    }
    if (filtersValue.discount_type) {
      preparedFilters.discount_type = filtersValue.discount_type;
    }
    if (filtersValue.stock === false) {
      preparedFilters.stock = false;
    }

    void dispatch(
      getDiscountList({
        vid: vendorId,
        cycle_id: Number(params.cycleId),
        page_size: pageSize ? pageSize : pagination.pageSize,
        page: page ? page : pagination.page,
        ...preparedFilters
      })
    );
  };

  const loadMore = () => {
    getItems({pageSize: pagination.pageSize, page: pagination.page + 1});
    setPagination({pageSize: pagination.pageSize, page: pagination.page + 1});
  };

  const removeFilter = (name: string) => {
    if (name === 'category') {
      setFilters((current) =>
        current.map((filter) =>
          name === filter.name
            ? {...filter, items: filter.items?.map((item) => ({...item, selected: false}))}
            : filter
        )
      );
      return;
    }
    if (name === 'sort') {
      setFiltersValue((current) => ({...current, sort: {}}));
      return;
    }
    if (name === 'stock') {
      setFiltersValue((current) => ({...current, stock: {}}));
    }
  };

  useEffect(() => {
    void dispatch(getFilters({vendor_id: vendorId, cycle_id: params.cycleId}));
    void dispatch(
      getDiscountList({
        vid: vendorId,
        cycle_id: Number(params.cycleId),
        page_size: pagination.pageSize,
        page: 1
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (categoriesList?.length) {
      setFilters((current) =>
        current.map((filter) =>
          filter.name === 'category' ? {...filter, items: categoriesList} : filter
        )
      );
    }
  }, [categoriesList]);

  useEffect(() => {
    setPagination({pageSize: pagination.pageSize, page: 1});
    getItems({pageSize: pagination.pageSize, page: 1});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValue.discount_type, filtersValue.discount_share_type]);

  if (loading) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  return (
    <PageLayout
      id="container"
      footer={
        campaignCycle?.state === 'in_edsc' ? (
          <Button
            endIcon={<EditOutlined aria-hidden />}
            fullWidth
            onClick={() => {
              navigate(DISCOUNT_VIEW_ROUTES.campaign.replace(':cycleId', String(params.cycleId)));
            }}
          >
            ویرایش کالاها
          </Button>
        ) : undefined
      }
    >
      <DiscountViewInfo />
      <Divider className="bg-gray-02" size={8} />
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="text-subtitle-md text-neutral-fg">کالا‌های شامل تخفیف</span>
        <span className="text-body-sm text-neutral-fg">{campaignCycle?.total_items} کالا</span>
      </div>
      <PromotionFilters
        handleFilter={handleFilter}
        activeFilterItem={activeFilterItem}
        filters={filters}
        handleRadioChange={handleRadioChange}
        filtersValue={filtersValue}
        getItems={getItems}
        removeFilter={removeFilter}
        setPagination={setPagination}
        state={campaignCycle?.cycle?.state || ''}
      />
      <InfiniteScroll
        dataLength={campaignCycle?.products?.length || 0}
        scrollableTarget="container"
        next={loadMore}
        hasMore={campaignCycle?.products?.length < campaignCycle?.total_items}
        loader={
          <div className="flex h-10 justify-center">
            <Loading size="medium" color="primary" />
          </div>
        }
        scrollThreshold={0.6}
      >
        <div className={loading ? 'pointer-events-none opacity-50' : undefined}>
          {campaignCycle?.products?.map((product: {id: number | string}) => (
            <DiscountViewProductItem key={product.id} item={product} />
          ))}
        </div>
      </InfiniteScroll>
    </PageLayout>
  );
};

export default DiscountView;
